import { Badge, Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconRepeat } from '@tabler/icons-react';
import { TEST_OFFER_BADGE, SUPPORT_EMAIL, formatRubles, type MySubscriptionView } from '@creativitas/shared';
import { toUserMessage } from '@/lib/api';
import {
  CHECKOUT_PRODUCTS_KEY,
  MY_SUBSCRIPTIONS_KEY,
  cancelMySubscription,
  fetchMySubscriptions,
} from '@/features/admin/payments-api';

/**
 * Автопродление в профиле (оферта, п. 7.2.1; docs/payments/RECURRING.md).
 *
 * Условия и дата следующего списания видны всегда (п. 5.2.1), отключение — одна
 * кнопка и подтверждение: без анкет и звонков. Доступ после отключения остаётся
 * до конца оплаченного срока. Нет и не было автопродления — карточки нет.
 */

const date = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Moscow' }).replace(/\s?г\.$/, '');

export function RecurringCard() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: MY_SUBSCRIPTIONS_KEY, queryFn: fetchMySubscriptions });
  const cancel = useMutation({
    mutationFn: cancelMySubscription,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: CHECKOUT_PRODUCTS_KEY });
      notifications.show({ color: 'teal', message: 'Автопродление отключено — подтверждение придёт на почту' });
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  const items = query.data?.items ?? [];
  const active = items.find((item) => item.status === 'active');
  const last = active ?? items[0];
  if (!last) return null;

  const confirm = (sub: MySubscriptionView) =>
    modals.openConfirmModal({
      title: 'Отключить автопродление?',
      children: (
        <Text size="sm" c="var(--text-muted)">
          Новых списаний не будет. Уже оплаченный доступ сохранится до конца срока
          {sub.nextChargeAt ? ` — до ${date(sub.nextChargeAt)}` : ''}. Подключить снова можно при следующей оплате.
        </Text>
      ),
      labels: { confirm: 'Отключить', cancel: 'Оставить' },
      confirmProps: { color: 'red' },
      onConfirm: () => cancel.mutate(sub.id),
    });

  return (
    <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }} data-testid="recurring-card">
      <Stack gap="xs">
        <Group gap="xs">
          <IconRepeat size={18} />
          <Title order={4}>Автопродление</Title>
          <Badge variant="light" color={last.status === 'active' ? 'teal' : 'gray'}>
            {last.status === 'active' ? 'включено' : 'отключено'}
          </Badge>
          {last.test ? <Badge variant="light" color="grape">{TEST_OFFER_BADGE}</Badge> : null}
        </Group>
        <Text size="sm">
          {last.title}: <b>{formatRubles(last.amountKopecks)}</b> каждые {last.days} дн. тем же способом, что и первая оплата.
        </Text>
        {last.status === 'active' ? (
          <>
            {last.charging ? (
              <Text size="sm">Списание отправлено — ждём подтверждения банка.</Text>
            ) : last.retryAt ? (
              <Text size="sm" c="var(--warning-text, orange)">
                Последнее списание не прошло. Повторим {date(last.retryAt)} — проверьте, что карта или счёт действуют и на них достаточно средств.
              </Text>
            ) : last.nextChargeAt ? (
              <Text size="sm">Следующее списание — <b>{date(last.nextChargeAt)}</b>.</Text>
            ) : null}
            <Text size="xs" c="var(--text-muted)">
              Отключить можно в любой момент — доступ сохранится до конца оплаченного срока. Или напишите на {SUPPORT_EMAIL}.
            </Text>
            <Group>
              <Button size="xs" variant="light" color="red" loading={cancel.isPending} onClick={() => confirm(last)}>
                Отключить автопродление
              </Button>
            </Group>
          </>
        ) : (
          <Text size="sm" c="var(--text-muted)">
            Отключено{last.endedAt ? ` ${date(last.endedAt)}` : ''}{last.endReason ? `: ${last.endReason}` : ''}. Списаний больше не будет.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
